import { cn } from "@/lib/cn";
import type * as React from "react";

const SIZES = {
  default: "tw-h-5 tw-w-9",
  sm: "tw-h-4 tw-w-[26px]",
  md: "tw-h-6 tw-w-11",
  lg: "tw-h-7 tw-w-[50px]",
  // The send flow's 32×18 switch (Figma 198:304526): #88819f when off.
  compact: "tw-h-[18px] tw-w-8 tw-bg-blue-500",
};

const DOT_SIZES = {
  default: "tw-size-4",
  sm: "tw-size-[11px]",
  md: "tw-size-5",
  lg: "tw-size-6",
  compact: "tw-size-3.5 tw-bg-[#faf8ff]",
};

interface ToggleProps {
  checked?: boolean;
  onCheckedChange?: (checked: boolean) => void;
  size?: keyof typeof SIZES;
  disabled?: boolean;
  disabledCallback?: () => void;
  className?: string;
  dotClassName?: string;
  "aria-label"?: string;
}

const Switch = ({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) => {
  return (
    <div
      className={cn("tw-flex tw-items-center tw-justify-between", className)}
    >
      {children}
    </div>
  );
};

const Toggle = ({
  checked = false,
  onCheckedChange,
  size = "default",
  disabled = false,
  disabledCallback,
  className,
  dotClassName,
  "aria-label": ariaLabel,
}: ToggleProps) => {
  const handleToggle = () => {
    if (disabled) return disabledCallback?.();
    const newValue = !checked;
    onCheckedChange?.(newValue);
  };

  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={ariaLabel}
      onClick={handleToggle}
      className={cn(
        "tw-flex tw-h-5 tw-w-9 tw-items-center tw-justify-start tw-rounded-full tw-border-none tw-bg-[#A69FB6] tw-p-0.5 tw-outline-none tw-transition-colors",
        SIZES[size],
        checked && "tw-bg-primary",
        disabled && "tw-cursor-not-allowed tw-opacity-40",
        className,
      )}
    >
      <span
        className={cn(
          "tw-inline-block tw-size-4 tw-rounded-full tw-bg-white tw-transition-transform",
          checked && "tw-translate-x-full",
          DOT_SIZES[size],
          dotClassName,
        )}
      />
    </button>
  );
};

const Label = ({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) => {
  return (
    <span
      className={cn(
        "tw-font-sans tw-text-[11px]/normal tw-font-normal tw-text-blue-800",
        className,
      )}
    >
      {children}
    </span>
  );
};

Switch.Toggle = Toggle;
Switch.Label = Label;

export default Switch;
